import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type SettingsSaveStatus = 'idle' | 'saved' | 'error';
export type SettingsSaveSnapshot = Readonly<{
  resetKey: string;
  serializedValue: string;
}>;

export const useSettingsSaveState = <T>(value: T, resetKey: string) => {
  const snapshot = useMemo(() => JSON.stringify(value), [value]);
  const latestSnapshot = useRef(snapshot);
  const latestResetKey = useRef(resetKey);
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SettingsSaveStatus>('idle');

  latestSnapshot.current = snapshot;
  latestResetKey.current = resetKey;

  useEffect(() => {
    setSavedSnapshot(null);
    setSaveStatus('idle');

    const resetTimer = window.setTimeout(() => {
      setSavedSnapshot(latestSnapshot.current);
    }, 0);

    return () => window.clearTimeout(resetTimer);
  }, [resetKey]);

  const hasUnsavedChanges =
    savedSnapshot !== null && snapshot !== savedSnapshot;
  const isInitialized = savedSnapshot !== null;

  useEffect(() => {
    if (hasUnsavedChanges && saveStatus === 'saved') {
      setSaveStatus('idle');
    }
  }, [hasUnsavedChanges, saveStatus]);

  const captureSaveSnapshot = useCallback(
    (): SettingsSaveSnapshot => ({ resetKey, serializedValue: snapshot }),
    [resetKey, snapshot],
  );

  // Legacy async callers retain this callback before saving. Keep its defaults
  // tied to that render so completion cannot acknowledge later edits or stores.
  const markSaved = useCallback(
    (submittedSnapshot: SettingsSaveSnapshot = {
      resetKey,
      serializedValue: snapshot,
    }) => {
      if (submittedSnapshot.resetKey !== latestResetKey.current) return;

      setSavedSnapshot(submittedSnapshot.serializedValue);
      setSaveStatus('saved');
    },
    [resetKey, snapshot],
  );

  const markSaveFailed = useCallback(() => {
    setSaveStatus('error');
  }, []);

  return {
    hasUnsavedChanges,
    isInitialized,
    saveStatus,
    captureSaveSnapshot,
    markSaved,
    markSaveFailed,
  };
};
